import { useEffect, useMemo, useRef } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/api";
import { useSocketStore } from "@/store/useSocketStore";

type VisibilityRealtimeInvalidationParams = {
  brandId?: string;
  days: number;
  providers?: string[];
  enabled?: boolean;
  queryKeys: QueryKey[];
};

type VisibilitySocketFilters = {
  brandId: string;
  days: "1" | "7" | "14" | "30" | "90";
  providers?: string;
  includeActionables?: boolean;
  includeRunResponses?: boolean;
};

type VisibilitySocketPayload = {
  brandId: string;
  filters?: VisibilitySocketFilters;
};

const buildFiltersSignature = (filters: VisibilitySocketFilters) =>
  `${filters.brandId}:${filters.days}:${filters.providers ?? ""}::${
    filters.includeActionables ? "1" : "0"
  }:${filters.includeRunResponses === false ? "0" : "1"}`;

const normalizeDays = (days: number): VisibilitySocketFilters["days"] => {
  const allowed = new Set(["1", "7", "14", "30", "90"]);
  const normalized = String(days);
  return allowed.has(normalized)
    ? (normalized as VisibilitySocketFilters["days"])
    : "30";
};

const normalizeProviders = (providers?: string[]) => {
  if (!providers?.length) return undefined;
  return providers
    .map((provider) => provider.trim())
    .filter(Boolean)
    .map((provider) => provider.toUpperCase())
    .join(",");
};

export const useVisibilityRealtimeInvalidation = ({
  brandId,
  days,
  providers,
  enabled = true,
  queryKeys,
}: VisibilityRealtimeInvalidationParams) => {
  const queryClient = useQueryClient();
  const queryKeysRef = useRef(queryKeys);
  const ensureSocket = useSocketStore((state) => state.ensureSocket);
  const addVisibilitySubscription = useSocketStore(
    (state) => state.addVisibilitySubscription,
  );
  const removeVisibilitySubscription = useSocketStore(
    (state) => state.removeVisibilitySubscription,
  );

  useEffect(() => {
    queryKeysRef.current = queryKeys;
  }, [queryKeys]);

  const filters = useMemo<VisibilitySocketFilters | null>(() => {
    if (!brandId) return null;
    return {
      brandId,
      days: normalizeDays(days),
      providers: normalizeProviders(providers),
      includeActionables: true,
    };
  }, [brandId, days, providers]);

  useEffect(() => {
    if (!enabled || !filters) return;

    const signature = buildFiltersSignature(filters);
    const socket = ensureSocket(API_BASE_URL);
    const subscribePayload = {
      brandId: filters.brandId,
      days: filters.days,
      providers: filters.providers,
      includeActionables: true,
    };

    const invalidateQueries = (payload: VisibilitySocketPayload) => {
      if (!payload?.filters) return;
      const payloadSignature = buildFiltersSignature(payload.filters);
      if (payloadSignature !== signature) return;
      queryKeysRef.current.forEach((queryKey) => {
        void queryClient.invalidateQueries({
          queryKey,
          refetchType: "active",
        });
      });
    };

    socket.on("visibility:results", invalidateQueries);
    socket.on("visibility:actionables", invalidateQueries);
    addVisibilitySubscription(signature, subscribePayload);

    return () => {
      socket.off("visibility:results", invalidateQueries);
      socket.off("visibility:actionables", invalidateQueries);
      removeVisibilitySubscription(signature);
    };
  }, [
    enabled,
    filters,
    queryClient,
    ensureSocket,
    addVisibilitySubscription,
    removeVisibilitySubscription,
  ]);
};
